/**
 * HeaderFeedbackButton
 *
 * Global feedback entry point from the app header.
 * - Opens as an anchored, chat-style pop-up (not a generic centered modal) so its purpose
 *   ("talk to us") reads instantly - Issue 11 (Overall Platform/Home): the previous version
 *   was a bare speech-bubble icon with no visible label, easily mistaken for chat/notifications.
 * - Allows attaching files (client-side; names included in submitted comment)
 * - Enforces at least one mandatory field (feedback message)
 * - Provides a link out to an external/full feedback form if configured
 */

import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquarePlus, Paperclip, X, Send, Camera } from 'lucide-react';
import { Button } from '../UI/Button';
import { useFeedbackStore } from '../../stores/feedbackStore';
import { sendFeedbackEmailNotification, sendFeedbackEmailWithAttachments } from '../../api/feedback';
import { useToastStore } from '../../stores/toast';
import { useConfig } from '../../contexts/ConfigContext';
import { capturePageScreenshot } from '../../utils/capturePageScreenshot';

/** Fallback recipients used only when config.customization.feedbackRecipients is not set. */
const FEEDBACK_FALLBACK_EMAILS: string[] = [];

export interface HeaderFeedbackButtonProps {
  /** Optional project ID to associate global feedback with */
  projectId?: string;
}

export const HeaderFeedbackButton: React.FC<HeaderFeedbackButtonProps> = ({
  projectId,
}) => {
  const config = useConfig();
  const feedbackToEmails = config?.customization?.feedbackRecipients ?? FEEDBACK_FALLBACK_EMAILS;
  /** Product label for subjects/copy — never hardcode Keystone on CSM/SPARC. */
  const productName =
    config?.branding?.feedbackProductName?.trim() ||
    config?.branding?.platformTitle?.trim() ||
    config?.branding?.appName?.trim() ||
    'TraceKit';
  const [isOpen, setIsOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [messageTouched, setMessageTouched] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);

  const { submitFeedback, isSubmitting } = useFeedbackStore();
  const { addToast } = useToastStore();

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setIsOpen(false);
    setSubject('');
    setMessage('');
    setMessageTouched(false);
    setAttachments([]);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: File[] = [];
    for (let i = 0; i < files.length; i += 1) {
      newFiles.push(files.item(i)!);
    }

    setAttachments((prev) => [...prev, ...newFiles]);

    // Reset the input so the same file can be re-selected if needed
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * One-click viewport screenshot → attach as image (same pipeline as Add files).
   * Hides the feedback dialog during capture so it does not appear in the shot.
   */
  const handleScreenshotPage = async () => {
    if (isCapturingScreenshot || isSubmitting || isSendingEmail) return;

    setIsCapturingScreenshot(true);
    try {
      // Unmount dialog first so capture sees the underlying page.
      // Short settle is handled inside capturePageScreenshot as well.
      await new Promise<void>((resolve) => {
        // Two rAFs + a tick: layout settles after portal unmount.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 50);
          });
        });
      });

      const file = await capturePageScreenshot({
        settleMs: 40,
        hideSelectors: ['[data-feedback-capture-root]'],
      });
      setAttachments((prev) => [...prev, file]);
      addToast({
        type: 'success',
        title: 'Screenshot attached',
        message: 'Current page screenshot added to your feedback.',
        duration: 3000,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[HeaderFeedbackButton] Screenshot capture failed:', error);
      addToast({
        type: 'error',
        title: 'Screenshot failed',
        message: 'Could not capture the page. You can still attach a file manually.',
        duration: 5000,
      });
    } finally {
      setIsCapturingScreenshot(false);
    }
  };

  const handleSubmit = async () => {
    setMessageTouched(true);

    if (!message.trim()) {
      return;
    }

    // Comment for backend storage: message + optional attachment names only (no size/type text)
    const attachmentNames =
      attachments.length > 0 ? attachments.map((f) => f.name).join(', ') : '';
    const feedbackCommentLines: string[] = [];
    if (subject.trim()) {
      feedbackCommentLines.push(`Subject: ${subject.trim()}`);
    }
    feedbackCommentLines.push(message.trim());
    if (attachmentNames) {
      feedbackCommentLines.push('');
      feedbackCommentLines.push(`Attachments: ${attachmentNames}`);
    }

    const feedback = {
      query_id: `header-feedback-${Date.now()}`,
      project_id: projectId || 'global',
      feedback_type: 'report_issue' as const,
      comment: feedbackCommentLines.join('\n'),
      timestamp: new Date().toISOString(),
    };

    try {
      // Try to submit feedback to backend (non-blocking - if it fails, we still send email)
      try {
        await submitFeedback(feedback as any);
      } catch (backendError) {
        // Backend submission failed, but we'll still send email
        // eslint-disable-next-line no-console
        console.warn('[HeaderFeedbackButton] Backend submission failed, continuing with email:', backendError);
      }

      // Send email: with real file attachments when user attached files, otherwise plain message.
      // Backend adds sign-off "Thanks, <username>" to the email body.
      setIsSendingEmail(true);
      try {
        const emailPayload = {
          subject: subject.trim() || `Feedback from ${productName}`,
          message: message.trim(),
          feedbackId: feedback.query_id,
          projectId: projectId || 'global',
          toEmail: feedbackToEmails.join(','),
        };
        const emailResponse =
          attachments.length > 0
            ? await sendFeedbackEmailWithAttachments({
                ...emailPayload,
                files: attachments,
              })
            : await sendFeedbackEmailNotification(emailPayload);

        if (emailResponse.success) {
          addToast({
            type: 'success',
            title: 'Feedback sent successfully',
            message: 'Your feedback has been sent to the team via email.',
            duration: 5000
          });
        }
      } catch (emailError) {
        // eslint-disable-next-line no-console
        console.error('[HeaderFeedbackButton] Failed to send email notification:', emailError);
        addToast({
          type: 'error',
          title: 'Email failed',
          message: 'Failed to send feedback email. Please try again.',
          duration: 5000
        });
        setIsSendingEmail(false);
        return; // Don't close modal if email fails
      } finally {
        setIsSendingEmail(false);
      }

      handleClose();
    } catch (error) {
      // This catch should rarely be hit now, but keep it for safety
      // eslint-disable-next-line no-console
      console.error('[HeaderFeedbackButton] Unexpected error:', error);
      addToast({
        type: 'error',
        title: 'Unexpected error',
        message: 'An unexpected error occurred. Please try again.',
        duration: 5000
      });
    }
  };

  const messageError = messageTouched && !message.trim();

  const canUseDOM =
    typeof window !== 'undefined' && typeof document !== 'undefined';

  return (
    <>
      {/* Pill button with icon + visible label - a bare icon read as an ambiguous chat/notification
          glyph (Issue 11); the label makes the button self-explanatory without relying on the
          hover-only tooltip. */}
      <button
        type="button"
        onClick={handleOpen}
        className={`
          flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full border
          text-sm font-medium
          text-primary-700 dark:text-primary-300
          border-primary-200 dark:border-primary-800
          bg-primary-50 dark:bg-primary-900/20
          hover:bg-primary-100 dark:hover:bg-primary-900/40
          transition-all duration-200
          focus:ring-2 focus:ring-blue-500 focus:outline-none focus:ring-offset-2
          ${isOpen ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
        `}
        title="Send feedback"
        aria-label="Send feedback"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        data-testid="header-feedback-button"
      >
        <MessageSquarePlus className="h-4 w-4" aria-hidden />
        <span>Feedback</span>
      </button>

      {canUseDOM &&
        isOpen &&
        !isCapturingScreenshot &&
        createPortal(
          // Backdrop: click anywhere outside the chat bubble to dismiss, mirrors NotificationDropdown.
          <div
            className="fixed inset-0 z-50"
            data-feedback-capture-root="true"
            onClick={handleClose}
            role="presentation"
          >
            <div
              className="absolute top-16 right-8 w-[26rem] max-w-[calc(100vw-2rem)]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Speech-bubble tail pointing back up at the header button, reinforcing the
                  "chat with us" framing this issue asked for. */}
              <div
                className="absolute -top-2 right-8 h-4 w-4 rotate-45 bg-white dark:bg-slate-800 border-l border-t border-theme-border"
                aria-hidden
              />
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Send feedback"
                className="relative flex max-h-[80vh] flex-col overflow-hidden rounded-2xl border border-theme-border bg-white dark:bg-slate-800 shadow-2xl"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 border-b border-theme-border bg-primary-50 dark:bg-primary-900/20 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
                      <MessageSquarePlus className="h-4 w-4" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-theme-text">Send us feedback</h3>
                      <p className="mt-0.5 text-xs text-theme-text-secondary">
                        Found a bug, have an idea, or just want to say hi? Tell us here.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-md p-1 text-theme-text-secondary hover:bg-theme-surface-hover hover:text-theme-text"
                    aria-label="Close feedback"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
                  <div>
                    <label className="block text-sm font-medium text-theme-text mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none bg-theme-paper text-sm dark:text-black"
                      placeholder="Short summary of your feedback"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-text mb-1">
                      Your message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onBlur={() => setMessageTouched(true)}
                      rows={4}
                      className="w-full px-3 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none bg-theme-paper text-sm resize-none dark:text-black"
                      placeholder="Describe what happened, what you expected, and any other details that will help us."
                    />
                    {messageError && (
                      <p className="mt-1 text-xs text-red-600">
                        Please provide some details so we can act on your
                        feedback.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-text mb-1">
                      Attachments (optional)
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        leftIcon={<Paperclip className="w-4 h-4" />}
                        disabled={isCapturingScreenshot}
                      >
                        Add files
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleScreenshotPage}
                        leftIcon={<Camera className="w-4 h-4" />}
                        isLoading={isCapturingScreenshot}
                        disabled={isCapturingScreenshot || isSubmitting || isSendingEmail}
                        data-testid="feedback-screenshot-page"
                        title="Capture the current page and attach it"
                      >
                        Screenshot page
                      </Button>
                      <span className="text-xs text-slate-500 basis-full sm:basis-auto">
                        Screenshots or documents, stored with your feedback.
                      </span>
                    </div>

                    {attachments.length > 0 && (
                      <ul className="mt-2 space-y-1 text-xs text-theme-text">
                        {attachments.map((file, index) => (
                          <li
                            key={`${file.name}-${index.toString()}`}
                            className="flex items-center justify-between gap-2 bg-theme-elevated px-2 py-1 rounded"
                          >
                            <span className="truncate">
                              {file.name}{' '}
                              <span className="text-slate-400">
                                ({Math.round(file.size / 1024)} KB)
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(index)}
                              className="text-xs text-red-500 hover:text-red-600"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 border-t border-theme-border bg-theme-surface px-5 py-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit}
                    isLoading={isSubmitting || isSendingEmail}
                    disabled={isSubmitting || isSendingEmail || messageError}
                    rightIcon={!isSubmitting && !isSendingEmail ? <Send className="w-3.5 h-3.5" /> : undefined}
                  >
                    {isSendingEmail ? 'Sending...' : isSubmitting ? 'Submitting...' : 'Send feedback'}
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default HeaderFeedbackButton;
